import uuid from "uuid-random";
import {
  AssignationInstanceInput,
  AssignationSessionsPreviewInput,
  Block,
  Day,
  Interval,
  ScheduleInstance,
} from "@models/ISchema";
import type {
  SessionAssignationFilters,
  SessionAssignationRow,
  SessionAssignationRowPatch,
  SessionAssignationRowValues,
} from "@services/types/session-assignation/sessionAssignationTypes";

export class SessionAssignationError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = "SessionAssignationError";
  }
}

export const DAY_BY_ID: Readonly<Record<number, Day>> = {
  1: Day.Monday,
  2: Day.Tuesday,
  3: Day.Wednesday,
  4: Day.Thursday,
  5: Day.Friday,
  6: Day.Saturday,
  7: Day.Sunday,
};

export const calendarDate = (value: string): string => {
  const date = value?.slice(0, 10) ?? "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new SessionAssignationError("INVALID_DATE");
  const parsed = new Date(`${date}T00:00:00Z`);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) {
    throw new SessionAssignationError("INVALID_DATE");
  }
  return date;
};

export const dayIdForDate = (date: string): number => {
  const weekday = new Date(`${calendarDate(date)}T00:00:00Z`).getUTCDay();
  return weekday === 0 ? 7 : weekday;
};

export const normalizeTime = (time: string): string => {
  if (!/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(time ?? "")) {
    throw new SessionAssignationError("INVALID_TIME");
  }
  return time.length === 5 ? `${time}:00` : time;
};

export const intervalForDate = (date: string, weeks: readonly Interval[]): Interval => {
  const day = calendarDate(date);
  const matches = weeks.filter(
    week => day >= calendarDate(week.startingDate) && day <= calendarDate(week.endingDate),
  );
  if (matches.length !== 1 || matches[0].isInTerm === false) {
    throw new SessionAssignationError("DATE_OUTSIDE_LOADED_WEEKS");
  }
  return matches[0];
};

export const orderedBlocks = (blocks: readonly Block[]): Block[] =>
  [...blocks].sort(
    (a, b) =>
      a.day.localeCompare(b.day) ||
      a.startingTime.localeCompare(b.startingTime) ||
      a.endingTime.localeCompare(b.endingTime) ||
      a.id.localeCompare(b.id, undefined, { numeric: true }),
  );

export const blocksForTime = (
  date: string,
  startingTime: string,
  endingTime: string,
  catalog: readonly Block[],
): Block[] => {
  const day = DAY_BY_ID[dayIdForDate(date)];
  const start = normalizeTime(startingTime);
  const end = normalizeTime(endingTime);
  if (start >= end) throw new SessionAssignationError("INVALID_TIME_RANGE");
  const blocks = orderedBlocks(
    catalog.filter(
      block =>
        block.day === day &&
        normalizeTime(block.startingTime) >= start &&
        normalizeTime(block.endingTime) <= end,
    ),
  );
  if (
    !blocks.length ||
    normalizeTime(blocks[0].startingTime) !== start ||
    normalizeTime(blocks[blocks.length - 1].endingTime) !== end
  ) {
    throw new SessionAssignationError("TIME_OUTSIDE_BLOCK_CATALOG");
  }
  return blocks;
};

export const normalizeRowPatch = (
  row: SessionAssignationRowValues,
  patch: SessionAssignationRowPatch,
  weeks: readonly Interval[],
  blocks: readonly Block[],
): SessionAssignationRowValues => {
  const next = { ...row, ...patch };
  const dateChanged = patch.date !== undefined && patch.date !== row.date;
  const scheduleChanged =
    dateChanged ||
    patch.scheduleMode !== undefined ||
    patch.startingTime !== undefined ||
    patch.endingTime !== undefined;
  const date = calendarDate(next.date);
  const intervalId = dateChanged ? intervalForDate(date, weeks).id : row.intervalId;
  if (next.scheduleMode === "unscheduled") {
    return {
      ...next,
      date,
      intervalId,
      dayId: null,
      startingTime: null,
      endingTime: null,
      blocksIds: [],
    };
  }
  if (!scheduleChanged) return next;
  const selectedBlocks = blocksForTime(date, next.startingTime, next.endingTime, blocks);
  return {
    ...next,
    date,
    intervalId,
    dayId: dayIdForDate(date),
    startingTime: normalizeTime(next.startingTime),
    endingTime: normalizeTime(next.endingTime),
    blocksIds: selectedBlocks.map(block => block.id),
    blocksCountInput: String(selectedBlocks.length),
  };
};

export const sortOccurrences = (rows: readonly SessionAssignationRow[]): SessionAssignationRow[] =>
  [...rows].sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      Number(a.scheduleMode === "scheduled") - Number(b.scheduleMode === "scheduled") ||
      (a.startingTime ?? "").localeCompare(b.startingTime ?? "") ||
      (a.endingTime ?? "").localeCompare(b.endingTime ?? "") ||
      a.localId.localeCompare(b.localId),
  );

export const adaptScheduleInstances = (
  instances: readonly ScheduleInstance[],
  createId: () => string = uuid,
): SessionAssignationRow[] => {
  const rows: SessionAssignationRow[] = [];
  for (const instance of instances) {
    if (!instance?.interval?.id) throw new SessionAssignationError("INCOMPLETE_SCHEDULE_DATA");
    const date = calendarDate(instance.date);
    for (const section of instance.sections ?? []) {
      if (!section?.id) throw new SessionAssignationError("INCOMPLETE_SCHEDULE_DATA");
      for (const session of section.sessions ?? []) {
        if (!session?.id) throw new SessionAssignationError("INCOMPLETE_SCHEDULE_DATA");
        const blocks = (session.blocks ?? []).filter(Boolean);
        const scheduled = !!blocks.length;
        if (scheduled && (!instance.startingTime || !instance.endingTime)) {
          throw new SessionAssignationError("INCOMPLETE_SCHEDULE_DATA");
        }
        rows.push({
          localId: createId(),
          origin: {
            sessionId: session.id,
            sectionId: section.id,
            intervalId: instance.interval.id,
          },
          pendingState: "unchanged",
          sectionId: section.id,
          date,
          intervalId: instance.interval.id,
          scheduleMode: scheduled ? "scheduled" : "unscheduled",
          dayId: scheduled ? dayIdForDate(date) : null,
          startingTime: scheduled ? normalizeTime(instance.startingTime) : null,
          endingTime: scheduled ? normalizeTime(instance.endingTime) : null,
          blocksIds: blocks.map(block => block.id),
          blocksCountInput: String(session.blocksCount ?? (scheduled ? blocks.length : "")),
          classrooms: (session.classrooms ?? []).filter(Boolean),
          instructors: (session.instructors ?? []).filter(Boolean),
        });
      }
    }
  }
  return sortOccurrences(rows);
};

export const idSetKey = (ids: readonly string[]): string =>
  JSON.stringify([...new Set(ids)].sort());

export const occurrenceKey = (row: SessionAssignationRowValues): string =>
  JSON.stringify([
    row.sectionId,
    row.intervalId,
    row.scheduleMode,
    row.scheduleMode === "scheduled"
      ? [row.dayId, row.startingTime, row.endingTime, idSetKey(row.blocksIds)]
      : row.blocksCountInput.trim() && Number.isInteger(Number(row.blocksCountInput))
        ? Number(row.blocksCountInput)
        : row.blocksCountInput,
    idSetKey(row.classrooms.map(resource => resource.id)),
    idSetKey(row.instructors.map(resource => resource.id)),
  ]);

export const sameRowValues = (
  a: SessionAssignationRowValues,
  b: SessionAssignationRowValues,
): boolean => occurrenceKey(a) === occurrenceKey(b) && a.date === b.date;

export const visibleOccurrences = (
  rows: readonly SessionAssignationRow[],
  filters: SessionAssignationFilters,
): SessionAssignationRow[] =>
  sortOccurrences(
    rows.filter(
      row =>
        (!filters.sectionId || row.sectionId === filters.sectionId) &&
        (!filters.fromDate || row.date >= filters.fromDate) &&
        (!filters.toDate || row.date <= filters.toDate),
    ),
  );

export const duplicateOccurrencesCount = (rows: readonly SessionAssignationRow[]): number => {
  const keys = rows.filter(row => row.pendingState !== "deleted").map(occurrenceKey);
  return keys.length - new Set(keys).size;
};

const parseBlocksCount = (text: string): number => {
  const count = Number(text);
  // GraphQL Int serialization limits only. Zero/negative business values reach the preview.
  if (!text.trim() || !Number.isInteger(count) || count < -2147483648 || count > 2147483647) {
    throw new SessionAssignationError("BLOCKS_COUNT_NOT_GRAPHQL_INT");
  }
  return count;
};

const toPreviewInstance = (row: SessionAssignationRow): AssignationInstanceInput => ({
  date: row.date,
  intervalId: row.intervalId,
  originSessionId: row.origin?.sessionId ?? undefined,
  classroomIds: row.classrooms.map(resource => resource.id),
  instructorIds: row.instructors.map(resource => resource.id),
  ...(row.scheduleMode === "scheduled"
    ? {
        dayId: row.dayId,
        startingTime: row.startingTime,
        endingTime: row.endingTime,
        blocksIds: [...row.blocksIds],
      }
    : { blocksCount: parseBlocksCount(row.blocksCountInput), blocksIds: [] }),
});

export const buildAssignationPreviewInput = (
  linkId: string,
  sectionIds: readonly string[],
  rows: readonly SessionAssignationRow[],
): AssignationSessionsPreviewInput => {
  const bySection = new Map(sectionIds.map(id => [id, [] as AssignationInstanceInput[]]));
  for (const row of sortOccurrences(rows)) {
    if (row.pendingState === "deleted") continue;
    const instances = bySection.get(row.sectionId);
    if (!instances) throw new SessionAssignationError("SECTION_NOT_IN_LINK");
    instances.push(toPreviewInstance(row));
  }
  return {
    linkId,
    instancesBySection: [...bySection].map(([sectionId, instances]) => ({ sectionId, instances })),
  };
};
